<template>
  <div class="form-section">
    <ReviewAndSubmitComponent />
    <div className="section" v-for="(section, index1) in summaryContent" :key="section.title">
      <h3 class="review-subheading">{{ section.title }}</h3>
      <FormField :required="false" overrideClass="sectionTitle align-checkbox-top" :helperText="section.infoText ? section.infoText : ''">
        <table class="review-and-submit-table">
          <template role="table" :aria-label="`${section.title} summary`" :aria-rowcount="section.values.count" v-for="(item, index2) in section.values" :key="index2">
            <tr
              v-if="(item.value !== '' && item.value !== undefined) || item.diff === FormFieldUpdateStatus.REMOVED"
              v-bind:class="{
                item,
                update: item.diff === FormFieldUpdateStatus.UPDATED,
              }"
            >
              <!-- highlightRemoved: item.diff === FormFieldUpdateStatus.REMOVED, -->
              <!-- <tr hidden role="row">
              <span role="columnheader" aria-sort="none">Field Title</span>
              <span role="columnheader" aria-sort="none">Field Value</span>
            </tr> -->
              <td role="row" v-if="item.type && item.type === 'checkbox'" :aria-rowindex="getRowIndex(index1, index2)">
                <input role="cell" type="checkbox" :checked="item.value" disabled />
                <label role="cell" class="checkDisabled" :aria-label="getAriaLabel(item.value, item.diff)" v-html="item.title"></label>
              </td>
              <template role="row" v-else class="section-row" :aria-rowindex="getRowIndex(index1, index2)">
                <th role="cell" class="bold">{{ item.title }}</th>
                <td v-html="item.value" role="cell" :aria-label="getAriaLabel(item.value, item.diff)" v-bind:class="{ bold: item.diff === FormFieldUpdateStatus.UPDATED }" />
              </template>
            </tr>
          </template>
        </table>
      </FormField>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import { UpdatePaymentDetailsStepNames } from '@/enums';
import { CollectionMethods, FormFieldUpdateStatus, PaymentMethods } from '@general/js/enums';
import { VUEX_PAYMENT_DETAILS } from '@/constants/VuexConstants';
import { BSBMask, reverseFormatFormDate } from '@general/js/utils';
import { sentenceCase, convertDateToCCExpiry } from '@/utils';
import { isEmptyString } from '@general/js/utils/StringTransformUtils';
import ReviewAndSubmitComponent from '@/components/ReviewAndSubmitComponent.vue';
import { convertIpsiMaskedPanToCreditCardNumberDisplay } from '@/utils/DisplayUtils';
import PaymentDetailsMixin from './PaymentDetailsMixin';

export default {
  name: UpdatePaymentDetailsStepNames.step3,
  mixins: [PaymentDetailsMixin],
  components: { ReviewAndSubmitComponent },
  props: {
    initialData: {
      type: Object,
    },
    referenceData: {
      type: Object,
    },
    /**
     * The member (object) to view/update details for
     */
    selectedMember: {
      type: Object,
    },
  },

  data() {
    return {
      FormFieldUpdateStatus,
    };
  },

  methods: {
    ...mapMutations('PaymentDetails', {
      updateHasFormBeenUpdated: VUEX_PAYMENT_DETAILS.MUTATIONS.updateHasFormBeenUpdated,
    }),

    /**
     * Returns a diff object checking if the left value has been updated, removed or not changed
     * based on the comparison to the right object
     */
    diffUpdatedValues(left, right) {
      this.updateHasFormBeenUpdated(false);
      const diffObjects = (lhs, rhs) => {
        const diffResult = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const key of Object.keys(lhs)) {
          // Diff array values
          if (Array.isArray(lhs[key]) && Array.isArray(rhs[key])) {
            diffResult[key] = rhs[key].reduce((diff, _, index) => {
              // If we have more then initial lhs array then value must have been added
              if (index > lhs[key].length - 1) {
                diff.push(FormFieldUpdateStatus.UPDATED);
                this.updateHasFormBeenUpdated(true);
              } // Array value type is array or object
              else if (
                (Array.isArray(lhs[key][index]) && Array.isArray(rhs[key][index])) ||
                (typeof rhs[key][index] === 'object' && lhs[key][index] !== null && rhs[key][index] !== null)
              ) {
                diff.push(diffObjects(lhs[key][index], rhs[key][index]));
              } // Primitive value in array, so compare
              else if (lhs[key][index] !== rhs[key][index]) {
                diff.push(FormFieldUpdateStatus.UPDATED);
                this.updateHasFormBeenUpdated(true);
              } else {
                diff.push(FormFieldUpdateStatus.NO_CHANGE);
              }

              return diff;
            }, []);

            // If user has removed values, pad the result array with removed
            if (lhs[key].length > rhs[key].length) {
              diffResult[key] = diffResult[key].concat(Array(lhs[key].length - rhs[key].length).fill(FormFieldUpdateStatus.REMOVED));
            }
          } else if (lhs[key] == null && rhs[key] == null) {
            // Catch any scenarios where some permutation off null/undefined for both sides
            diffResult[key] = FormFieldUpdateStatus.NO_CHANGE;
          } else if (lhs[key] == null && rhs[key] !== null) {
            // Catch scenarios where initial data is null (siutations where initial is invoice and member switches to bank account)
            diffResult[key] = {};
            // eslint-disable-next-line no-restricted-syntax
            diffResult[key] = FormFieldUpdateStatus.UPDATED;
            // If initial data is null only, there has been an update
            this.updateHasFormBeenUpdated(true);
          } else if (lhs[key] !== null && rhs[key] !== null && typeof rhs[key] === 'object') {
            // Diff object values
            diffResult[key] = diffObjects(lhs[key], rhs[key]);
          } else if (lhs[key] !== '' && rhs[key] === '') {
            // Removed
            diffResult[key] = FormFieldUpdateStatus.REMOVED;
            this.updateHasFormBeenUpdated(true);
          } else if (key in rhs === false) {
            // Removed
            diffResult[key] = FormFieldUpdateStatus.REMOVED;
            this.updateHasFormBeenUpdated(true);
          } else if (JSON.stringify(lhs[key])?.toLowerCase()?.replace(/\s/g, '') !== JSON.stringify(rhs[key])?.toLowerCase()?.replace(/\s/g, '')) {
            // Comparing data in string without any white space
            // Updated value
            diffResult[key] = FormFieldUpdateStatus.UPDATED;
            this.updateHasFormBeenUpdated(true);
          } else {
            // No change
            diffResult[key] = FormFieldUpdateStatus.NO_CHANGE;
          }
        }
        // Mark any new object key/value pairs added to rhs that are not in lhs as updated
        const lhsSideKeys = Object.keys(lhs);
        const rhsSideKeys = Object.keys(rhs);
        const newRhsKeys = rhsSideKeys.filter((rhsKey) => {
          return !lhsSideKeys.includes(rhsKey);
        });

        newRhsKeys.forEach((rhsKey) => {
          diffResult[rhsKey] = FormFieldUpdateStatus.UPDATED;
          this.updateHasFormBeenUpdated(true);
        });

        return diffResult;
      };
      const diff = diffObjects(left, right);
      return diff;
    },

    paymentMethodName(paymentDetailsData) {
      const premiumPaymentAccount = paymentDetailsData.premiumPaymentAccount;
      const premiumPaymentMethod = paymentDetailsData.premiumPaymentMethod;
      if (premiumPaymentMethod && premiumPaymentMethod.collectionMethod === CollectionMethods.INVOICE) {
        return 'Invoice';
      }
      if (premiumPaymentAccount && premiumPaymentAccount.accountType === PaymentMethods.BANK_ACCOUNT) {
        return 'Bank account';
      }
      if (premiumPaymentAccount && premiumPaymentAccount.accountType === PaymentMethods.CREDIT_CARD) {
        return 'Credit card';
      }
      return '';
    },

    getPremiumPaymentSectionData(diffInformationObject) {
      const premiumPaymentAccount = this.paymentDetails.premiumPaymentAccount;
      const premiumPaymentMethod = this.paymentDetails.premiumPaymentMethod;
      const valuesArr = [];

      valuesArr.push(
        {
          title: 'Payment method',
          value: this.paymentMethodName(this.paymentDetails),
          diff:
            diffInformationObject?.premiumPaymentAccount?.accountType === FormFieldUpdateStatus.UPDATED ||
            diffInformationObject?.premiumPaymentMethod?.collectionMethod === FormFieldUpdateStatus.UPDATED
              ? FormFieldUpdateStatus.UPDATED
              : FormFieldUpdateStatus.NO_CHANGE,
        },
        {
          title: "I'd like to pay my premium",
          value: sentenceCase(premiumPaymentMethod.frequency),
          diff: diffInformationObject?.premiumPaymentMethod?.frequency === FormFieldUpdateStatus.UPDATED ? FormFieldUpdateStatus.UPDATED : FormFieldUpdateStatus.NO_CHANGE,
        },
      );

      if (premiumPaymentMethod && premiumPaymentMethod.collectionMethod === CollectionMethods.INVOICE) {
        valuesArr.push(
          {
            title: 'Invoice due date',
            // value: reverseFormatFormDate(premiumPaymentMethod.paymentStartDate), // this is disabled because, if payment start date has not been updated in Step 1, we need to show `nextContributionDate` instead (Fix for DE2E-7721)
            value:
              this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate') === FormFieldUpdateStatus.UPDATED
                ? reverseFormatFormDate(premiumPaymentMethod.paymentStartDate)
                : reverseFormatFormDate(this.paymentDetails.nextContributionDate),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate'),
          },
          {
            title: 'BPAY Biller code',
            value: this.referenceData.premiumPaymentInvoiceBpayBillerCode.informationText,
            diff: FormFieldUpdateStatus.NO_CHANGE,
          },
          {
            title: 'BPAY Customer reference no.',
            value: this.paymentDetails.premiumPaymentMethod.policyCrn,
            diff: FormFieldUpdateStatus.NO_CHANGE,
          },
        );
      } else if (premiumPaymentAccount && premiumPaymentAccount.accountType === PaymentMethods.BANK_ACCOUNT) {
        valuesArr.push(
          {
            title: 'Start date of regular direct debit',
            // value: reverseFormatFormDate(premiumPaymentMethod.paymentStartDate), // this is disabled because, if payment start date has not been updated in Step 1, we need to show `nextContributionDate` instead (Fix for DE2E-7721)
            value:
              this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate') === FormFieldUpdateStatus.UPDATED
                ? reverseFormatFormDate(premiumPaymentMethod.paymentStartDate)
                : reverseFormatFormDate(this.paymentDetails.nextContributionDate),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate'),
          },
          {
            title: 'BSB',
            value: BSBMask(premiumPaymentAccount.bsb),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'bsb'),
          },
          {
            title: 'Account number',
            value: premiumPaymentAccount.accountNumberOrToken,
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'accountNumberOrToken'),
          },
          {
            title: 'Account name',
            value: premiumPaymentAccount.accountHolderName,
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'accountHolderName'),
          },
        );
      } else if (premiumPaymentAccount && premiumPaymentAccount.accountType === PaymentMethods.CREDIT_CARD) {
        valuesArr.push(
          {
            title: 'Start date of regular direct debit',
            // value: reverseFormatFormDate(premiumPaymentMethod.paymentStartDate), // this is disabled because, if payment start date has not been updated in Step 1, we need to show `nextContributionDate` instead (Fix for DE2E-7721)
            value:
              this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate') === FormFieldUpdateStatus.UPDATED
                ? reverseFormatFormDate(premiumPaymentMethod.paymentStartDate)
                : reverseFormatFormDate(this.paymentDetails.nextContributionDate),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentMethod, 'paymentStartDate'),
          },
          {
            title: 'Name on card',
            value: premiumPaymentAccount.accountHolderName,
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'accountHolderName'),
          },
          {
            title: 'Card number',
            value: convertIpsiMaskedPanToCreditCardNumberDisplay(premiumPaymentAccount.maskedCreditCardNumber),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'maskedCreditCardNumber'),
          },
          {
            title: 'CVV',
            value: '***',
            // there's no way to check for cvv updates as of now because we don't have the required data points
            // below is based on assumption that, cvv is only changed when card number changes
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'maskedCreditCardNumber'),
          },
          {
            title: 'Expiry',
            value: convertDateToCCExpiry(premiumPaymentAccount.expiryDate),
            diff: this.getDiffStatus(diffInformationObject.premiumPaymentAccount, 'expiryDate'),
          },
        );
      }

      // Retrieving Result array with removed values
      const resultArr = [];
      const isPaymentMethodUpdated =
        diffInformationObject?.premiumPaymentAccount?.accountType === FormFieldUpdateStatus.UPDATED ||
        diffInformationObject?.premiumPaymentMethod?.collectionMethod === FormFieldUpdateStatus.UPDATED;

      const initialPaymentAccount = this.initialData.premiumPaymentAccount;
      const initialPaymentMethod = this.initialData.premiumPaymentMethod;

      // Map of title values to their initial data values
      const initialDataMap = {
        'Payment method': this.paymentMethodName(this.initialData),
        "I'd like to pay my premium": sentenceCase(initialPaymentMethod?.frequency),
        'Start date of regular direct debit': reverseFormatFormDate(initialPaymentMethod?.paymentStartDate),
        'Invoice  due date': reverseFormatFormDate(initialPaymentMethod?.paymentStartDate),
        BSB: BSBMask(initialPaymentAccount?.bsb),
        'Account number': initialPaymentAccount?.accountNumberOrToken,
        'Account name': initialPaymentAccount?.bsb ? initialPaymentAccount?.accountHolderName : null,
        'Name on card': initialPaymentAccount?.maskedCreditCardNumber ? initialPaymentAccount?.accountHolderName : null,
        'Card number': initialPaymentAccount?.maskedCreditCardNumber,
        Expiry: convertDateToCCExpiry(initialPaymentAccount?.expiryDate),
        CVV: isPaymentMethodUpdated ? null : '***',
      };

      // Map of initial data premium payment account values to title
      const premiumPaymentAccountToTitleMap = {
        accountNumberOrToken: 'Account number',
        accountHolderName: 'Account name',
        maskedCreditCardNumber: 'Card number',
        expiryDate: 'Expiry',
        bsb: 'BSB',
      };

      // Append initial values of updated items as a 'REMOVED' value
      valuesArr.forEach((item) => {
        if (item.diff === FormFieldUpdateStatus.UPDATED && !isEmptyString(initialDataMap[item.title])) {
          const removedItem = {
            title: item.title,
            diff: FormFieldUpdateStatus.REMOVED,
            // Retrieving initial value from title
            value: initialDataMap[item.title],
          };
          resultArr.push(removedItem);
        }
        resultArr.push(item);
      });

      // Update previous payment account details if payment method has changed
      if (isPaymentMethodUpdated) {
        const accountData = this.initialData.premiumPaymentAccount;
        Object.keys(accountData).forEach((key) => {
          // Retrieving title from key
          const mappedTitle = premiumPaymentAccountToTitleMap[key];
          if (!isEmptyString(initialDataMap[mappedTitle])) {
            const removedItem = {
              title: mappedTitle,
              diff: FormFieldUpdateStatus.REMOVED,
              // Retrieving initial value from title
              value: initialDataMap[mappedTitle],
            };
            resultArr.push(removedItem);
          }
        });
      }

      // Add checkbox for Bank acc/Credit card methods
      if (premiumPaymentMethod && premiumPaymentMethod.collectionMethod !== CollectionMethods.INVOICE) {
        // direct debit declaration box
        resultArr.push({
          title:
            this.addRequiredMark(this.refData.reviewAndSubmitDirectDebitAcknowledgementText.informationText) + this.refData.reviewAndSubmitPrivacyInformationText.informationText,
          value: true,
          type: 'checkbox',
        });
      }

      const dataUpdatedWithDiff = {
        title: 'Premium payment',
        values: resultArr,
        infoText: '',
      };

      return dataUpdatedWithDiff;
    },

    getClaimsSectionData(diffObject) {
      const claimsDiff = diffObject.claimsAccount;
      const claimsData = this.paymentDetails.claimsAccount;
      const claimsExtraData = this.paymentDetails?.claimsExtraData;

      const valuesArr = [];

      // Add reuse questions to review and submit table
      if (claimsExtraData?.isSameAsPremiumAccount) {
        valuesArr.push({
          title: 'Would you like to use the same account you nominated for premium payments?',
          value: 'Yes',
          diff: FormFieldUpdateStatus.UPDATED,
        });
      }
      if (claimsExtraData?.isSameAsPolicyHolder) {
        valuesArr.push({
          title: 'Would you like to copy the same account details currently nominated for the policy holder?',
          value: 'Yes',
          diff: FormFieldUpdateStatus.UPDATED,
        });
      }

      valuesArr.push(
        {
          title: 'BSB',
          value: BSBMask(claimsData?.bsb),
          diff: this.getDiffStatus(claimsDiff, 'bsb'),
        },
        {
          title: 'Account number',
          value: claimsData?.accountNumberOrToken,
          diff: this.getDiffStatus(claimsDiff, 'accountNumberOrToken'),
        },
        {
          title: 'Account name',
          value: claimsData?.accountHolderName,
          diff: this.getDiffStatus(claimsDiff, 'accountHolderName'),
        },
      );

      // Retrieving Result array with removed values
      const initialClaimsData = this.initialData.claimsAccount;
      const claimsTitleToInitialDataMap = {
        BSB: BSBMask(initialClaimsData?.bsb),
        'Account number': initialClaimsData?.accountNumberOrToken,
        'Account name': initialClaimsData?.accountHolderName,
      };

      const resultsArr = [];
      valuesArr.forEach((item) => {
        if (item.diff === FormFieldUpdateStatus.UPDATED && !isEmptyString(claimsTitleToInitialDataMap[item.title])) {
          // If fields are updated, add removed initial values
          const removedItem = {
            title: item.title,
            value: claimsTitleToInitialDataMap[item.title],
            diff: FormFieldUpdateStatus.REMOVED,
          };
          resultsArr.push(removedItem);
        }
        resultsArr.push(item);
      });

      // Add direct debit declaration box
      resultsArr.push({
        title: this.addRequiredMark(this.refData.claimsAccountAuthorisationText.informationText),
        value: true,
        type: 'checkbox',
      });

      const dataUpdatedWithDiff = {
        title: 'Claims account',
        values: resultsArr,
        infoText: '',
      };
      return dataUpdatedWithDiff;
    },

    getDiffStatus(diffObject, key) {
      return diffObject === FormFieldUpdateStatus.UPDATED || diffObject?.[key] === FormFieldUpdateStatus.UPDATED ? FormFieldUpdateStatus.UPDATED : FormFieldUpdateStatus.NO_CHANGE;
    },

    getRowIndex(index1, index2) {
      if (index1 === 0) {
        if (index2 !== 0) {
          return index2 + 1;
        }

        return 1;
      }
      return `${index1 + 1}${index2 + 1}`;
    },
    getAriaLabel(value, diffStatus) {
      if (diffStatus === FormFieldUpdateStatus.NO_CHANGE) {
        return value;
      }
      return `${value} ${diffStatus?.toLowerCase()}`;
    },
  },

  computed: {
    summaryContent() {
      const paymentDetails = cloneDeep(this.paymentDetails);

      const diff = this.diffUpdatedValues(this.initialData, paymentDetails);

      let tiles = [];

      // premium payment details tile is only shown for PH
      if (this.isPolicyHolderDetails) {
        const premiumPaymentTile = this.getPremiumPaymentSectionData(diff);
        tiles.push(premiumPaymentTile);
      }

      const claimsTile = this.getClaimsSectionData(diff);
      tiles.push(claimsTile);

      tiles = tiles.filter((tile) => {
        return !isEmpty(tile);
      });

      return tiles;
    },
  },
};
</script>

<style lang="scss" scoped>
.sectionTitle {
  :deep(.item1) {
    label {
      border-top: 1px solid $GREY_700;
      border-bottom: 1px solid $GREY_700;
      padding: 10px 20px;
      margin: 0px;
      font-weight: $BOLD;
    }
  }

  @include tablet-and-above {
    margin-bottom: 30px;
  }

  @include mobile {
    margin-bottom: 25px;

    :deep(.item3) {
      @include grid-position(0, 4);
      margin-bottom: 30px;
    }
  }
}

input[type='checkbox'] + label {
  &::before,
  &::after {
    top: rem(10);
  }
}
.table {
  .item {
    display: flex;
    padding: 5px 20px;

    .section-row {
      display: flex;
      flex-grow: 1;
      overflow-wrap: break-word;
      gap: 40px;

      > * {
        width: 50%;
        word-break: break-word;
      }
    }

    .bold {
      font-weight: $BOLD;
    }

    .card-spacing {
      margin-top: 10px;
    }
  }
}

.form-field {
  :deep(.helper-text) {
    p {
      margin-bottom: 0px;

      @include tablet-and-above {
        margin-top: 0px;
      }
    }

    @include tablet-and-below {
      margin-top: 10px;
      margin-bottom: 0px;
    }
  }
}

.highlightUpdated {
  background-color: $PRIMARY_GREEN_TINT;
}

.highlightRemoved {
  background-color: $LIGHT_CRIMSON;
}

.highlightUpdatedWithPadding {
  padding-top: 10px !important;
}

.checkDisabled {
  color: $DISABLED_BTN_TEXT_COLOR;
}
</style>
